import { useCampaignEditorContext } from 'sections/onsite-campaigns/pages/CampaignEditor/contexts/CampaignEditorContext';
import { useCallback } from 'react';
import moment, { Moment } from 'moment';
import { DateCantBeInThePastRule } from 'sections/onsite-campaigns/pages/CampaignEditor/domain/Workflow/Rules/SplitTest/DateCantBeInThePastRule';
import { useTranslation } from 'react-i18next';
import { useWorkflowForm } from '../../contexts/CampaignWorkflowFormContext';
import { useSegmentSchedulingSelector } from '../Workflows/DisplayWebContent/state/selectors/useSegmentSchedulingSelector';
import { useCampaignSchedulingSelector } from '../../state/CampaignSettings/selectors/useCampaignSchedulingSelector';

export const endDateFormKey = 'segment-schedule-end';

const validate = (t, endDate: Date) => {
  if (DateCantBeInThePastRule({ date: endDate }).result === 'fail') {
    return {
      passed: false,
      validationMessage: t('onsite.editor.workflow.scheduling.inlineValidationMessages.endDateInThePast'),
    };
  }

  return {
    passed: true,
    validationMessage: undefined,
  };
};

export const useEndDate = (messageId: string) => {
  const { t } = useTranslation('campaigns');
  const { actions, state } = useWorkflowForm();
  const { dispatch } = useCampaignEditorContext();

  const onFocusChange = useCallback(
    ({ focused }) => {
      actions.setTouched(endDateFormKey, true);
      actions.setFocused(endDateFormKey, focused);
    },
    [actions],
  );

  const { startDate, endDate } = useSegmentSchedulingSelector(messageId) ?? {};
  const onDateChange = useCallback(
    (endDate: Moment) => {
      return dispatch({
        type: 'SET_SEGMENT_END_DATE',
        payload: { id: messageId, endDate: endDate?.toDate() },
      });
    },
    [dispatch, messageId],
  );

  const campaignSchedule = useCampaignSchedulingSelector();

  const validation = validate(t, endDate);
  let minDate = moment(new Date());
  if (startDate) {
    minDate = moment(startDate);
  } else if (campaignSchedule?.startDate) {
    minDate = moment(campaignSchedule.startDate);
  }

  const maxDate = campaignSchedule.endDate ? moment(campaignSchedule.endDate) : undefined;

  return {
    endDate: endDate ? moment(endDate) : undefined,
    onDateChange,
    onFocusChange,
    focused: state.focused[endDateFormKey],
    minDate,
    maxDate,
    validation,
  };
};
